import React from 'react';

import {
  isChunkLoadError,
  isReloadInitiated,
  onChunkReloadCancelled,
  reloadOnceForChunkError,
  restoreWarmupStylesheets,
} from './chunkReload';

// Resolves with the module once the stylesheets a warm-up failed to load have
// been requested again, as Vite's helper would have awaited them.
const afterWarmupStylesheets = <M>(module: M): Promise<M> =>
  restoreWarmupStylesheets().then(() => module);

/**
 * Drop-in replacement for `React.lazy` that recovers from deploy-skew chunk
 * failures at the source. When the dynamic import rejects because a content-
 * hashed chunk no longer exists on the current build, it triggers a single
 * reload to pick up the new build and keeps the component suspended (the
 * Suspense loader stays visible) until that navigation commits, so no error UI
 * flashes. If the navigation is cancelled (the page stayed behind a
 * beforeunload prompt) the original error reaches the nearest error boundary.
 * If the reload was already spent (the failure survived a reload, i.e. a
 * genuine problem), the rejection propagates to the route errorElement.
 */
export function lazyWithReload<T extends React.ComponentType<any>>( // eslint-disable-line @typescript-eslint/no-explicit-any -- mirror React.lazy so any page component (with its own props) is accepted
  factory: () => Promise<{ default: T }>
): React.LazyExoticComponent<T> {
  return React.lazy(() =>
    factory().then(afterWarmupStylesheets, (error: unknown) => {
      if (isChunkLoadError(error)) {
        if (
          reloadOnceForChunkError(error, 'lazy_import') ||
          isReloadInitiated()
        ) {
          // Reload is coming; hold the loader until it lands, or surface the
          // error if the page turns out to stay.
          return new Promise<{ default: T }>((_resolve, reject) => {
            onChunkReloadCancelled(() => reject(error));
          });
        }
      }
      throw error;
    })
  );
}
